// BHP-1062: the live Hetzner state behind a VPS, from `GET /api/vps/{uuid}/server`
// (BHP-1101). Plain JSON, not Hydra — it is a computed payload, not a resource.
//
// This is deliberately separate from VpsSummary. That one is our own row:
// identity, hostname, labels, owner, and it is what the sync keeps in step with
// Hetzner (BHP-1067). This one is fetched fresh every time someone looks,
// because status and IP addresses are stale the moment they are stored.

/** Every field is nullable: Hetzner's payload is not a contract we control. */
export interface VpsServerState {
  vpsUuid: string;
  status: string | null;
  publicIpv4: string | null;
  publicIpv6: string | null;
  serverType: string | null;
  datacenter: string | null;
  createdAt: string | null;
  cores: number | null;
  memoryGb: number | null;
  diskGb: number | null;
  /**
   * BHP-1103: the recurring monthly price for the billed customer, excluding
   * VAT — the billing catalog price times that customer's margin, which is the
   * same figure the order line carries for a full month.
   *
   * Null when the backend cannot produce an honest figure: unknown server
   * type, no billed customer, or a catalog row priced at zero. Never render
   * that as 0.00 — the bug this replaced was a page confidently showing a
   * price nobody was billed.
   */
  monthlyPriceNet: number | null;
  currency: string;
}

/**
 * Formats the monthly price, or returns null when there is none to show.
 *
 * Pinned to nl-NL rather than the viewer's locale: this is the amount that
 * turns up on a Dutch invoice, so it should read the way the invoice does.
 */
export function formatMonthlyPriceNet(
  state: Pick<VpsServerState, 'monthlyPriceNet' | 'currency'> | undefined
): string | null {
  const amount = state?.monthlyPriceNet;

  if (amount === null || amount === undefined) {
    return null;
  }

  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: state?.currency ?? 'EUR'
  }).format(amount);
}

export const METRIC_TYPES = ['cpu', 'disk', 'network'] as const;
export type MetricType = (typeof METRIC_TYPES)[number];

export const METRIC_PERIODS = ['15m', '3h', '24h'] as const;
export type MetricPeriod = (typeof METRIC_PERIODS)[number];

export const DEFAULT_PERIOD: MetricPeriod = '15m';

/** Hetzner returns `[unixSeconds, value]` pairs, the value as a string. */
type TimeSeriesPoint = [number, string];

interface TimeSeries {
  values?: TimeSeriesPoint[];
}

/**
 * `GET /api/vps/{uuid}/metrics?resourceType=&period=`. `metrics` is Hetzner's
 * payload passed straight through, so the series keys are theirs:
 * `cpu`, `disk.0.bandwidth.read`, `network.0.pps.in`, and so on.
 */
export interface VpsMetrics {
  vpsUuid: string;
  resourceType: string;
  period: string;
  metrics: {
    time_series?: Record<string, TimeSeries>;
  };
}

/** Which series to plot per metric type, in the order the legacy chart used. */
export const SERIES_KEYS: Record<MetricType, string[]> = {
  cpu: ['cpu'],
  disk: ['disk.0.bandwidth.read', 'disk.0.bandwidth.write'],
  network: ['network.0.pps.in', 'network.0.pps.out']
};

export interface ChartSeries {
  /** `HH:MM` labels, shared by every series in one chart. */
  labels: string[];
  points: Array<{ name: string; values: number[] }>;
}

/**
 * Flatten Hetzner's time series into what the chart needs.
 *
 * A missing series yields an empty chart rather than throwing: Hetzner omits
 * keys for hardware a server does not have, and one absent disk series should
 * not take down the whole detail page.
 */
export function toChartSeries(
  metrics: VpsMetrics | undefined,
  type: MetricType
): ChartSeries {
  const series = metrics?.metrics.time_series;
  if (series === undefined) {
    return { labels: [], points: [] };
  }

  const wanted = SERIES_KEYS[type].filter((key) => series[key] !== undefined);
  if (wanted.length === 0) {
    return { labels: [], points: [] };
  }

  const first = series[wanted[0]]?.values ?? [];

  return {
    labels: first.map(([seconds]) => timeLabel(seconds)),
    points: wanted.map((key) => ({
      name: key,
      values: (series[key]?.values ?? []).map(([, value]) => Number(value))
    }))
  };
}

function timeLabel(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toTimeString().slice(0, 5);
}
